import React, { useState } from 'react';

import Hurricane from '../../media/images/modules/hurricane.jpg';
import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Panel, Segments, Empty, useFlash } from '../../ui/kit.js';
import { useBuilding, day, clock, nextId } from '../../state/building.js';

const RECIPIENTS = 412;
const CHANNELS = ['Lobby screens', 'Resident app push', 'SMS', 'Email'];

const TEMPLATES = [
    { name: 'Fire alarm test', level: 'Advisory', title: 'Fire alarm test today, 10:00 to 11:00 AM', body: 'The fire alarm system will be tested floor by floor. Alarms may sound briefly. No action is needed unless you hear a continuous alarm with a voice instruction.' },
    { name: 'Water shutoff', level: 'Advisory', title: 'Water shutoff, floors 10 to 18', body: 'Domestic water will be off from 9:00 AM to 1:00 PM for a riser valve replacement. Please avoid running dishwashers or washing machines during this window.' },
    { name: 'Hurricane preparation', level: 'Emergency', title: 'Hurricane warning: secure your balcony now', body: 'A hurricane warning is in effect for Miami-Dade. Bring in all balcony furniture and plants by 6:00 PM. The garage will close at 8:00 PM and valet will stop at 6:00 PM. Hurricane shutters will be lowered on common areas tonight.' },
    { name: 'Elevator outage', level: 'Advisory', title: 'North elevator out of service', body: 'The north passenger elevator is out of service for repair. Please use the south elevators. Service elevator available on request at the front desk.' },
    { name: 'Shelter in place', level: 'Emergency', title: 'Shelter in place until further notice', body: 'Police are responding to an incident near the building. Stay inside your unit, keep doors locked and stay away from windows. Do not come down to the lobby. We will update you here.' }
];

const HISTORY = [
    { id: 1, level: 'advisory', title: 'Pool closed for resurfacing', sentAt: `${day(-3)}, 8:00 AM`, channels: ['Lobby screens', 'Resident app push', 'Email'], delivered: 409, read: 351 },
    { id: 2, level: 'emergency', title: 'Tropical storm watch: hurricane shutters test', sentAt: `${day(-12)}, 4:15 PM`, channels: CHANNELS, delivered: 411, read: 398 }
];

/* Emergency Communications: one message to every resident, on every
   channel, and on every resident screen in the building until it ends. */
function EmergencyScreen() {
    const { alert, setAlert } = useBuilding();
    const [flash, notify] = useFlash();
    const [level, setLevel] = useState('Advisory');
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [channels, setChannels] = useState(CHANNELS);
    const [history, setHistory] = useState(HISTORY);

    const applyTemplate = t => {
        setLevel(t.level);
        setTitle(t.title);
        setBody(t.body);
        setChannels(CHANNELS);
    };

    const toggle = channel => setChannels(list => (
        list.includes(channel) ? list.filter(c => c !== channel) : [...list, channel]
    ));

    const send = event => {
        event.preventDefault();
        if (!title.trim() || !body.trim()) return notify('Add a title and a message, or start from a template.');
        if (channels.length === 0) return notify('Choose at least one channel.');

        const sentAt = clock();
        const levelKey = level === 'Emergency' ? 'emergency' : 'advisory';

        setAlert({ level: levelKey, title, body, sentAt });
        setHistory(h => [{
            id: nextId(),
            level: levelKey,
            title,
            sentAt: `${day(0)}, ${sentAt}`,
            channels,
            delivered: RECIPIENTS - 2,
            read: Math.round(RECIPIENTS * 0.64)
        }, ...h]);
        setTitle('');
        setBody('');
        notify(`Sent to ${RECIPIENTS} residents by ${channels.join(', ')}.`);
    };

    const end = () => {
        setAlert(null);
        notify('Alert ended. Resident screens are back to normal.');
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Mass notification" title="Emergency communications" />
            {flash}

            <div className="banner">
                <img src={Hurricane} alt="" />
                <p className="banner__caption">Reach every resident on every channel in seconds.</p>
            </div>

            {alert ? (
                <Panel className="panel--accent" style={{ marginBottom: '1rem' }}>
                    <div className="row__end" style={{ justifyContent: 'space-between' }}>
                        <h4>{alert.title}</h4>
                        <Chip tone={alert.level === 'emergency' ? 'alert' : 'info'}>
                            {alert.level === 'emergency' ? 'Emergency live' : 'Advisory live'}
                        </Chip>
                    </div>
                    <p>{alert.body}</p>
                    <p className="row__meta">
                        Sent {alert.sentAt} to {RECIPIENTS} residents. This alert now shows on every resident screen (switch to Resident to see it).
                    </p>
                    <button type="button" className="btn btn-primary btn-sm" onClick={end}>End alert</button>
                </Panel>
            ) : (
                <Empty>No alert is live. Resident screens are showing their normal content.</Empty>
            )}

            <div className="split section-gap">
                <Panel as="form" className="panel--accent" onSubmit={send}>
                    <h4>Compose</h4>
                    <div className="form">
                        <div className="field field--wide">
                            <span className="form-label">Level</span>
                            <Segments label="Alert level" options={['Emergency', 'Advisory']} value={level} onChange={setLevel} />
                        </div>
                        <Field label="Title" wide>
                            <input className="form-control" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Water shutoff tomorrow" />
                        </Field>
                        <Field label="Message" wide>
                            <textarea className="form-control" rows="4" value={body} onChange={e => setBody(e.target.value)} placeholder="What residents need to know and do" />
                        </Field>
                        {CHANNELS.map(channel => (
                            <label className="field check" key={channel}>
                                <input type="checkbox" checked={channels.includes(channel)} onChange={() => toggle(channel)} style={{ width: 'auto' }} />
                                <span>{channel}</span>
                            </label>
                        ))}
                        <button type="submit" className="btn btn-primary">Send to all residents</button>
                    </div>
                    <p className="row__meta">
                        This platform supplements, and does not replace, the building's monitored fire alarm and life-safety systems.
                    </p>
                </Panel>

                <div className="stack">
                    <h4>Templates</h4>
                    <div className="cards">
                        {TEMPLATES.map((t, i) => (
                            <Panel key={t.name} index={i}>
                                <div className="row__end" style={{ justifyContent: 'space-between' }}>
                                    <h4>{t.name}</h4>
                                    <Chip tone={t.level === 'Emergency' ? 'alert' : 'info'}>{t.level}</Chip>
                                </div>
                                <p className="row__meta">{t.title}</p>
                                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => applyTemplate(t)}>Use template</button>
                            </Panel>
                        ))}
                    </div>

                    <h4>Sent</h4>
                    <ul className="rows">
                        {history.map(h => (
                            <li className="row" key={h.id}>
                                <div className="row__main">
                                    <span className="row__title">{h.title}</span>
                                    <span className="row__meta">
                                        {h.sentAt} · {RECIPIENTS} recipients · {h.delivered} delivered · {h.read} read · {h.channels.join(', ')}
                                    </span>
                                </div>
                                <div className="row__end">
                                    <Chip tone={h.level === 'emergency' ? 'alert' : 'info'}>
                                        {h.level === 'emergency' ? 'Emergency' : 'Advisory'}
                                    </Chip>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default EmergencyScreen;
